import type { DataElement, NaturalizedDataset } from 'dcmjs';
import {
    Charset,
    DEFAULT_CHARSET,
    UTF8_CHARACTER_SET,
    charsetFromDeclaration,
} from './charset';
import { DicomDataset, DicomElement, DicomItem } from './dataset';
import { tagForKeyword, vrForTag } from './dictionary';
import { DCMJS_IMPLEMENTATION, buildFileMeta } from './fileMeta';
import { DicomFile } from './reader';
import { PIXEL_DATA_TAG, SPECIFIC_CHARACTER_SET_TAG, Tag } from './tag';
import { EXPLICIT_VR_LITTLE_ENDIAN, transferSyntaxFor } from './transferSyntax';
import { Vr, isStringVr } from './vr';
import { formatNumericValue } from './writer';

/**
 * The inverse of the naturalized projection: rebuilds the engine's typed model
 * from a dataset in dcmjs's naturalized shape.
 *
 * This is what lets the engine's writer serve the application's save path,
 * whose input is a naturalized dataset that call sites have mutated in place.
 * Until those call sites hold the typed model directly, every save has to come
 * back through here.
 *
 * The projection is lossy in one direction that matters and cannot be undone
 * here: an element rebuilt from a naturalized value has no source bytes, so it
 * is re-encoded from its value rather than reproduced. The engine's guarantee
 * that an untouched attribute keeps its exact bytes therefore holds for a file
 * that is read and written through the typed model, and not for one that has
 * been round-tripped through the naturalized shape. Recovering it means moving
 * call sites onto the model, which is what this shim exists to make possible.
 */

/** Rebuilds a writable file from a naturalized dataset. */
export function fromNaturalizedDataset(natural: NaturalizedDataset): DicomFile {
    const transferSyntaxUid =
        readMetaTransferSyntax(natural) ?? EXPLICIT_VR_LITTLE_ENDIAN;
    const transferSyntax = transferSyntaxFor(transferSyntaxUid);

    // The transfer syntax has to be known before the dataset is built: under
    // an encapsulated syntax the pixel data's buffers are frames to be written
    // as separate fragments, and concatenating them would produce a file whose
    // frames can no longer be told apart.
    const dataset = denaturalizeDataset(natural, transferSyntax.encapsulated);
    const meta = buildFileMeta(
        dataset,
        transferSyntaxUid,
        DCMJS_IMPLEMENTATION
    );

    return {
        preamble: new Uint8Array(0),
        meta,
        dataset,
        transferSyntax,
    };
}

/**
 * Reads the transfer syntax out of a naturalized dataset's `_meta`.
 *
 * `_meta` holds data elements rather than naturalized values, so the value is
 * one level deeper than elsewhere. A dataset assembled in memory may have no
 * meta at all, in which case the caller falls back to explicit VR little
 * endian — the same default dcmjs applies.
 */
function readMetaTransferSyntax(
    natural: NaturalizedDataset
): string | undefined {
    const meta = natural._meta as
        | Record<string, DataElement | string | undefined>
        | undefined;
    const element = meta?.TransferSyntaxUID;
    if (element === undefined) {
        return undefined;
    }
    if (typeof element === 'string') {
        return element;
    }
    const value = element.Value?.[0];
    return typeof value === 'string' ? value : undefined;
}

/** Rebuilds one dataset, descending into sequences. */
export function denaturalizeDataset(
    natural: NaturalizedDataset,
    encapsulatedPixelData = false,
    dropped: string[] = [],
    inheritedCharset: Charset = DEFAULT_CHARSET
): DicomDataset {
    const dataset = new DicomDataset();
    const vrMap = (natural._vrMap ?? {}) as Record<string, string | undefined>;

    // The character set governs how text is encoded, and (0008,0005) sorts
    // before every attribute it applies to, so resolving it up front means the
    // elements are built under the set they will be written in.
    //
    // A sequence item carries no character set of its own: PS3.3 C.12.1.1.2
    // makes the enclosing dataset's apply to every dataset nested inside it.
    // Defaulting an item to the default repertoire instead would encode text
    // inside a sequence — an ROI name among them — in a different set from the
    // one the file declares.
    const declaredCharset = readCharsetDeclaration(natural);
    // Nothing declared, nothing inherited, and text that ASCII cannot hold:
    // the object was assembled by this application rather than read, and the
    // only faithful answer is a set that carries the content. Narrowing the
    // text is right when a legacy set was deliberately chosen for a receiving
    // system; it is wrong when nothing was chosen at all.
    const upgradeToUtf8 =
        declaredCharset === undefined &&
        inheritedCharset === DEFAULT_CHARSET &&
        needsMoreThanDefaultRepertoire(natural);

    dataset.setCharset(
        declaredCharset !== undefined
            ? charsetFromDeclaration(declaredCharset)
            : upgradeToUtf8
              ? charsetFromDeclaration(UTF8_CHARACTER_SET)
              : inheritedCharset
    );
    if (upgradeToUtf8) {
        // Declared as well as used: bytes saying one thing while the object
        // claims another is what makes a file unreadable to its recipient.
        dataset.setString(SPECIFIC_CHARACTER_SET_TAG, UTF8_CHARACTER_SET, 'CS');
    }

    for (const key of Object.keys(natural)) {
        if (key === '_vrMap' || key === '_meta') {
            continue;
        }
        const tag = tagForKeyword(key);
        if (tag === undefined) {
            // An attribute the dictionary has no keyword for — every private
            // attribute among them — is dropped, because that is what dcmjs's
            // denaturalization does and a writer replacing it must not change
            // what a saved file contains.
            //
            // It is a real loss: the application's saved files do not carry
            // the private attributes their sources did, which is why the
            // anonymizer re-injects them by hand after denaturalizing. Fixing
            // it means writing them, which changes saved output and belongs
            // with its own traceability rather than inside a swap meant to be
            // invisible. `droppedKeys` reports what went, so the loss is
            // visible to a caller instead of only to a log line.
            dropped.push(key);
            continue;
        }
        const element = buildElement(
            tag,
            natural[key],
            vrMap[key],
            dataset,
            encapsulatedPixelData,
            dropped
        );
        if (element !== undefined) {
            dataset.put(element);
        }
    }
    return dataset;
}

/** A naturalized dataset that cannot be expressed as DICOM. */
export class DenaturalizeError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'DenaturalizeError';
    }
}

/**
 * Whether any text in the dataset needs more than the default repertoire.
 *
 * An absent SpecificCharacterSet means ASCII, so a dataset the application
 * assembled from a patient's identity has no way to carry that patient's name
 * unless one is declared. Rather than narrowing the name to fit — which is
 * right when a legacy set was deliberately chosen, and wrong when nothing was
 * chosen at all — the declaration is upgraded to UTF-8 so the text survives.
 *
 * Scanning strings only: the numeric and date VRs are ASCII by definition, and
 * a structure set's bulk is contour coordinates the projection has already
 * turned into numbers, so there is little here to walk.
 */
function needsMoreThanDefaultRepertoire(value: unknown): boolean {
    if (typeof value === 'string') {
        // eslint-disable-next-line no-control-regex
        return /[^\u0000-\u007f]/.test(value);
    }
    if (Array.isArray(value)) {
        return value.some(needsMoreThanDefaultRepertoire);
    }
    if (value !== null && typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>).some(
            ([key, nested]) =>
                key !== '_meta' &&
                key !== '_vrMap' &&
                needsMoreThanDefaultRepertoire(nested)
        );
    }
    return false;
}

function readCharsetDeclaration(
    natural: NaturalizedDataset
): string | undefined {
    const declared: unknown = natural.SpecificCharacterSet;
    if (typeof declared === 'string') {
        return declared;
    }
    if (Array.isArray(declared)) {
        return declared.filter((part) => typeof part === 'string').join('\\');
    }
    return undefined;
}

/**
 * Builds one element from a value in the naturalized shape, for a caller
 * writing a single attribute into a typed dataset.
 *
 * The value's shape — a bare string, an array, a person name object, a nested
 * item — is interpreted exactly as it is for a whole dataset, because it is
 * the same routine. A second interpretation of those shapes is how a value
 * written through one path comes to differ from the same value written
 * through the other.
 *
 * Text is encoded in `target`'s character set, which for a sequence item is
 * the set the enclosing dataset declared.
 */
export function elementFromNaturalValue(
    tag: Tag,
    value: unknown,
    target: DicomDataset
): DicomElement | undefined {
    // Nothing is dropped for a caller writing one attribute it composed
    // itself: `dropped` collects keywords the dictionary does not know, and
    // these values are built from keywords in the code that writes them.
    return buildElement(tag, value, undefined, target, false, []);
}

/**
 * Builds one element from a naturalized value.
 *
 * The VR comes from `_vrMap` where the source recorded one — that is what the
 * map is for — and from the dictionary otherwise.
 */
function buildElement(
    tag: Tag,
    value: unknown,
    mappedVr: string | undefined,
    dataset: DicomDataset,
    encapsulatedPixelData: boolean,
    dropped: string[]
): DicomElement | undefined {
    const vr = resolveElementVr(tag, mappedVr);
    const values = toValueArray(value);

    if (encapsulatedPixelData && tag === PIXEL_DATA_TAG) {
        // The naturalized shape holds one buffer per frame and does not carry
        // the basic offset table, so the table cannot be restored. A single
        // zero entry is written, which is what dcmjs emits; PS3.5 Annex A.4
        // also allows an empty table, and a reader finding either walks the
        // fragments to locate frames.
        return DicomElement.fromFragments(tag, vr, {
            offsetTable: new Uint8Array(4),
            fragments: values
                .filter(isBinaryValue)
                .map(toBytes)
                .flatMap(splitIntoFragments),
        });
    }

    if (vr === 'SQ') {
        // The naturalized shape records no length framing, so there is nothing
        // to restore and a choice has to be made. Undefined length, for both
        // the sequence and each of its items, is the one dcmjs makes for
        // everything it writes, and matching it is what keeps this writer's
        // output identical to the output the application produces today.
        const items: DicomItem[] = values.map((item) => ({
            dataset: denaturalizeDataset(
                item as NaturalizedDataset,
                false,
                dropped,
                dataset.specificCharset
            ),
            undefinedLength: true,
        }));
        return DicomElement.fromItems(tag, items, true);
    }

    if (values.length === 0) {
        // An attribute present with no value is a type 2 attribute left empty;
        // it is written with a zero-length value rather than omitted.
        return DicomElement.fromBytes(tag, vr, new Uint8Array(0));
    }

    const buffers = values.filter(isBinaryValue);
    if (buffers.length > 0) {
        return DicomElement.fromBytes(tag, vr, concatBuffers(buffers));
    }

    if (isStringVr(vr)) {
        return DicomElement.fromStrings(
            tag,
            vr,
            values.map((item) => toDicomString(item, vr)),
            dataset.specificCharset
        );
    }
    return DicomElement.fromNumbers(
        tag,
        vr,
        values.map((item) => Number(item))
    );
}

function resolveElementVr(tag: Tag, mappedVr: string | undefined): Vr {
    if (mappedVr !== undefined) {
        const normalized = mappedVr.trim().toUpperCase();
        if (normalized.length === 2) {
            return normalized as Vr;
        }
    }
    return vrForTag(tag);
}

/**
 * Normalizes a naturalized value to the array of values it stands for.
 *
 * The naturalized shape unwraps a single value out of its array, so this is
 * where that is undone. A null value means the attribute is present but empty.
 */
function toValueArray(value: unknown): unknown[] {
    if (value === null || value === undefined) {
        return [];
    }
    if (Array.isArray(value)) {
        return [...value];
    }
    return [value];
}

/**
 * The fragment size dcmjs splits an encapsulated frame into.
 *
 * PS3.5 lets a frame occupy any number of fragments, so this is a choice
 * rather than a rule. It is matched here so that replacing the writer does not
 * change the structure of a file the application saves; the value has no
 * meaning beyond being the one dcmjs picked.
 */
const DCMJS_FRAGMENT_SIZE = 1024 * 20;

/**
 * Splits one frame into the fragments it is written as, padding the last one
 * to an even length as PS3.5 requires of every fragment.
 */
function splitIntoFragments(frame: Uint8Array): Uint8Array[] {
    const fragments: Uint8Array[] = [];
    for (let at = 0; at < frame.byteLength; at += DCMJS_FRAGMENT_SIZE) {
        fragments.push(
            frame.subarray(
                at,
                Math.min(at + DCMJS_FRAGMENT_SIZE, frame.byteLength)
            )
        );
    }
    if (fragments.length === 0) {
        return [];
    }
    const last = fragments[fragments.length - 1];
    if (last.byteLength % 2 !== 0) {
        const padded = new Uint8Array(last.byteLength + 1);
        padded.set(last);
        fragments[fragments.length - 1] = padded;
    }
    return fragments;
}

function isBinaryValue(value: unknown): value is ArrayBuffer | ArrayBufferView {
    return (
        value instanceof ArrayBuffer ||
        Object.prototype.toString.call(value) === '[object ArrayBuffer]' ||
        ArrayBuffer.isView(value)
    );
}

function concatBuffers(values: unknown[]): Uint8Array {
    const parts = values.map(toBytes);
    const total = parts.reduce((sum, part) => sum + part.byteLength, 0);
    const combined = new Uint8Array(total);
    let at = 0;
    for (const part of parts) {
        combined.set(part, at);
        at += part.byteLength;
    }
    return combined;
}

function toBytes(value: unknown): Uint8Array {
    if (ArrayBuffer.isView(value)) {
        return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    }
    return new Uint8Array(value as ArrayBuffer);
}

/**
 * Renders a naturalized value as the string its VR is written from.
 *
 * A person name arrives as `{ Alphabetic }`, and the numeric string VRs arrive
 * as numbers because the projection parsed them. Rendering a number back to
 * text is where a decimal string can change its spelling — "1.50" reads as
 * 1.5 and writes back as "1.5" — which is why an attribute that has been
 * through the naturalized shape cannot be promised its original bytes.
 */
function toDicomString(value: unknown, vr: Vr): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (value instanceof String) {
        return String.prototype.toString.call(value);
    }
    if (typeof value === 'object' && 'Alphabetic' in value) {
        const alphabetic = (value as { Alphabetic?: unknown }).Alphabetic;
        return alphabetic === undefined ? '' : String(alphabetic);
    }
    if (typeof value === 'number' && (vr === 'DS' || vr === 'IS')) {
        // The projection parsed these into numbers, so writing them means
        // spelling them again. The writer's own formatter is used so a value
        // too long for DS is re-spelled rather than cut short.
        return formatNumericValue(value, vr);
    }
    return String(value);
}
